import type {PlayerAPI} from 'bitmovin-player';
import {useEffect, FC} from 'react';
import {usePlayerContext} from '../../hooks/usePlayerContext';
import {useUiService} from '../../hooks/usePlayerState';
import {
  TrackSend,
  useAudioTrackService,
  useSubtitleTrackService,
} from '../../hooks/useTrackContext';
import {BigPlayButton} from '../bigPlayButton';
import {ControlBar} from '../controlBar/controlBar';
import {PanelContainer} from '../panelContainer';
import {SubtitleContainer} from '../subtitleContainer';
import * as Styles from './uiContainer.styles';

const refreshAudioTracks = (player: PlayerAPI, send: TrackSend): void => {
  send({type: 'resetTracks'});

  const audioTracks = player.getAvailableAudio();
  audioTracks.forEach((track) => {
    send({
      type: 'addedTrack',
      value: track,
    });
  });

  const curAudioTrack = player.getAudio();
  if (curAudioTrack) {
    send({
      type: 'selectedTrack',
      value: curAudioTrack,
    });
  }
};

const refreshSubtitleTracks = (player: PlayerAPI, send: TrackSend): void => {
  const subtitleTracks = player.subtitles.list();
  send({type: 'resetTracks'});

  subtitleTracks.forEach((track) => {
    send({
      type: 'addedTrack',
      value: track,
    });
  });
};

export const UiContainer: FC = () => {
  const {send} = useUiService();
  const {send: audioTrackSend} = useAudioTrackService();
  const {send: subtitleTrackSend} = useSubtitleTrackService();
  const {player} = usePlayerContext();

  useEffect(() => {
    if (!player) return;

    import('bitmovin-player').then((bm) => {
      const {PlayerEvent, WarningCode} = bm;

      player.on(PlayerEvent.SourceLoaded, () => {
        send({
          type: 'sourceLoaded',
          isLive: player.isLive(),
          duration: player.getDuration(),
          buffered: [],
        });

        refreshAudioTracks(player, audioTrackSend);
        refreshSubtitleTracks(player, subtitleTrackSend);

        const config = player.getConfig();

        // Initialize the state based on the player at source load time
        if (player.isMuted()) {
          send('mute');
        } else {
          send('unmute');
        }

        if (!config.playback?.autoplay) {
          send('noPlayback');
        }
      });

      player.on(PlayerEvent.Play, () => {
        send('startPlayback');
        send('play');
      });
      player.on(PlayerEvent.Playing, () => {
        send('startPlayback');
        send('play');
      });
      player.on(PlayerEvent.Paused, () => send('pause'));
      player.on(PlayerEvent.Muted, () => send('mute'));
      player.on(PlayerEvent.Unmuted, () => send('unmute'));
      player.on(PlayerEvent.SourceUnloaded, () => send('unloadSource'));

      player.on(PlayerEvent.Seek, (event) => {
        send({
          type: 'seek',
          value: event.seekTarget,
        });
      });

      player.on(PlayerEvent.Seeked, () => send('finishedSeeking'));

      // When playback attempt is reject with warning 5008, switch the player state to not playing.
      // This is required for blocked authoplay, becuase there is no Paused event in that case.
      player.on(PlayerEvent.Warning, (event) => {
        if (event.code === WarningCode.PLAYBACK_COULD_NOT_BE_STARTED) {
          send('noPlayback');
        }
      });

      player.on(PlayerEvent.TimeChanged, (event) => {
        send({
          type: 'updateTimestamp',
          value: event.time,
        });
      });

      player.on(PlayerEvent.DurationChanged, (event) => {
        send({
          type: 'updateDuration',
          value: event.to,
        });
      });

      player.on(PlayerEvent.VolumeChanged, (event) =>
        send({
          type: 'changeVolume',
          value: event.targetVolume,
        })
      );

      player.on(PlayerEvent.ViewModeChanged, (event) => {
        send(event.to === 'fullscreen' ? 'enterFullscreen' : 'exitFullscreen');
      });

      player.on(PlayerEvent.SegmentRequestFinished, () => {
        const buffered = player.getBufferedRanges();
        send({
          type: 'updateBuffered',
          value: buffered,
        });
      });

      player.on(PlayerEvent.AudioAdded, (event) => {
        audioTrackSend({
          type: 'addedTrack',
          value: event.track,
        });
      });

      player.on(PlayerEvent.AudioChanged, (event) => {
        audioTrackSend({
          type: 'selectedTrack',
          value: event.targetAudio,
        });
      });

      player.on(PlayerEvent.AudioRemoved, (event) => {
        audioTrackSend({
          type: 'removedTrack',
          value: event.track,
        });
      });

      player.on(PlayerEvent.SubtitleAdded, (event) => {
        subtitleTrackSend({
          type: 'addedTrack',
          value: event.subtitle,
        });
      });

      player.on(PlayerEvent.SubtitleRemoved, (event) => {
        subtitleTrackSend({
          type: 'removedTrack',
          value: event.subtitle,
        });
      });

      player.on(PlayerEvent.SubtitleEnabled, (event) => {
        subtitleTrackSend({
          type: 'selectedTrack',
          value: event.subtitle,
        });
      });

      player.on(PlayerEvent.SubtitleDisabled, (event) => {
        subtitleTrackSend({
          type: 'disabledTrack',
          value: event.subtitle,
        });
      });

      player.on(PlayerEvent.PeriodSwitched, () => {
        refreshAudioTracks(player, audioTrackSend);
        refreshSubtitleTracks(player, subtitleTrackSend);
      });
    });
  }, [player, send, audioTrackSend, subtitleTrackSend]);

  return (
    <Styles.Overlay>
      <BigPlayButton />
      <Styles.Controls>
        <SubtitleContainer />
        <PanelContainer />
        <ControlBar />
      </Styles.Controls>
    </Styles.Overlay>
  );
};
